import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { RiArrowLeftSLine, RiArrowRightSLine } from 'react-icons/ri';
import { t } from 'ttag';
import { Heading } from 'components';
import client from '../../js/utils/client';
import Product from './Product';

function ProductSlider({
  products,
  title,
  baseUrl,
  backgroundColor,
  backgroundImage,
  titleColor
}) {
  const [productData, setProductData] = useState(
    /** @type {{name: string, image: string, url: string, price: number, rrPrice: number, currency: string, sizesArray: string[]}[]} */ []
  );

  const [isLoading, toggleLoading] = useState(true);
  const productSlider = useRef(null);

  const navigateSlider = useCallback(direction => {
    if (productSlider.current) {
      if (direction === 'next')
        productSlider.current.scrollLeft += productSlider.current.clientWidth;
      if (direction === 'previous')
        productSlider.current.scrollLeft -= productSlider.current.clientWidth;
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const fetchProducts = () => {
      return client.get(
        baseUrl
          ? `${baseUrl}/api/products/batch/?list=${String(products)}`
          : `/api/products/batch/?list=${String(products)}`,
        {
          signal: controller.signal
        }
      );
    };

    fetchProducts().then(({ data }) => {
      toggleLoading(false);

      setProductData(
        data.products.map(product => ({
          name: product.name,
          image: product.image,
          url: product.relative_url,
          price: product.prices?.max_price,
          rrPrice: product.prices.recommended_retail_price,
          currency: product.prices?.currency,
          sizesArray: product.stock.map(size => size.name_short)
        }))
      );
    });

    return () => {
      controller.abort();
    };
  }, [products]);

  return (
    <div
      className={clsx('p-10')}
      style={{
        backgroundColor: backgroundColor ? `${backgroundColor}` : 'transparent',
        backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none'
      }}
    >
      <div className={clsx('flex items-start justify-between gap-4')}>
        {title && (
          <div className={clsx('m-2 font-semibold')}>
            <Heading as="h2" noMargin={true} color={titleColor}>
              {title}
            </Heading>
          </div>
        )}
        <div className={clsx('flex gap-4')}>
          <button
            className={clsx(
              'flex shrink-0 cursor-pointer items-center border-0 bg-light-white-smoke p-2 hover:bg-whisper'
            )}
            type="button"
            onClick={() => navigateSlider('previous')}
            aria-label={t`Previous`}
          >
            <RiArrowLeftSLine className={clsx('h-6 w-6')} />
          </button>
          <button
            className={clsx(
              'flex shrink-0 cursor-pointer items-center border-0 bg-light-white-smoke p-2 hover:bg-whisper'
            )}
            type="button"
            onClick={() => navigateSlider('next')}
            aria-label={t`Next`}
          >
            <RiArrowRightSLine className={clsx('h-6 w-6')} />
          </button>
        </div>
      </div>
      <div className="flex w-full items-center justify-between">
        <ul
          className={clsx(
            'hide-scrollbar flex w-full snap-x snap-mandatory gap-6 overflow-x-auto scroll-smooth p-0 before:w-[30vw] before:shrink-0 after:w-[30vw] after:shrink-0'
          )}
          ref={productSlider}
        >
          {isLoading &&
            products.map(productId => (
              <li
                className="flex w-28 shrink-0 animate-pulse flex-col items-center gap-2 bg-white p-5 md:w-40"
                key={productId}
              >
                <div className="h-28 w-28 rounded bg-whisper md:h-40 md:w-40" />
                <div className="lg:h-18 h-14 w-full rounded bg-whisper" />
                <div className="h-14 w-full rounded bg-whisper" />
                <div className="h-10 w-full rounded bg-whisper" />
              </li>
            ))}
          {productData.map(product => (
            <Product product={product} key={product.name} baseUrl={baseUrl} />
          ))}
        </ul>
      </div>
    </div>
  );
}

ProductSlider.propTypes = {
  /** An Array of Product IDs that should be rendered in the Product Slider */
  products: PropTypes.arrayOf(PropTypes.number).isRequired,
  /** Title of the component */
  title: PropTypes.string,
  /** A base url is correspondent site the product should be linked. eg.: https://www.unisportstore.com */
  baseUrl: PropTypes.string,
  /** Specifies the Text color of the Title. */
  titleColor: PropTypes.string,
  /** Specifies the Background Color of the Engage Component. */
  backgroundColor: PropTypes.string,
  /** Specifies the Background Image of the Engage Component. */
  backgroundImage: PropTypes.string
};

ProductSlider.defaultProps = {
  title: 'Title',
  baseUrl: 'https://www.unisportstore.com',
  backgroundColor: '#FFFFFF',
  backgroundImage: undefined,
  titleColor: '#fff'
};

export default ProductSlider;
